(ns app.front.view.admin.api-keys
  (:require ["@carbon/icons-react/es/edit/24" :default Edit24]
            [app.front.clipboard :as clipboard]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.config-pills :as ui-config-pills]
            [app.front.ui.modal :as ui-modal]
            [app.front.ui.section :as section]
            [app.front.ui.table :as ui-table]
            [app.front.util :as futil :refer [for-frag]]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-event-fx ::fetch-api-keys
  (fn [_ _]
    {:comm/command {:command    :admin/application-api-keys
                    :method     :get
                    :on-success [::entities/merge-entity-db]}}))

(rf/reg-event-fx ::add-api-key
  (fn [_ _]
    {:comm/command {:command    :admin/create-application-api-key
                    :method     :post
                    :on-success [::entities/merge-entity-db]
                    :notify     {:success :api-key-ui/add-new-success}}}))

(rf/reg-event-fx ::update-attribute
  (fn [_ [_ data]]
    {:comm/command {:command    :admin/update-application-api-key
                    :data       data
                    :on-success [::entities/apply-changes]
                    :notify     {:success :api-key-ui/update-success}}}))

(rf/reg-event-fx ::regenerate-client-secret
  (fn [_ [_ id]]
    {:comm/command {:command    :admin/update-application-api-key-secret
                    :data       {:db/id id}
                    :on-success [::entities/merge-entity-db]
                    :notify     {:success :api-key-ui/update-success}}}))

(rf/reg-event-fx ::delete-api-key
  (fn [_ [_ id]]
    {:comm/command {:command    :admin/delete-application-api-key
                    :data       {:db/id id}
                    :on-success [::entities/apply-changes]
                    :notify     {:success :api-key-ui/delete-success}}}))

(rf/reg-event-fx ::add-redirect-uri
  (fn [_ [_ data]]
    {:comm/command {:command    :admin/register-application-api-key-redirect-uri
                    :data       data
                    :on-success [::add-redirect-uri.success]
                    :notify     {:success :api-key-ui/update-success}}}))

(rf/reg-event-fx ::add-redirect-uri.success
  (fn [_ [_ status response]]
    {:fx [[:dispatch [::entities/apply-changes status response]]
          [:dispatch [::close-redirect-uri-modal]]]}))

(rf/reg-event-fx ::delete-redirect-uri
  (fn [_ [_ id uri]]
    {:comm/command {:command    :admin/delete-application-api-key-redirect-uri
                    :data       {:db/id id
                                 :uri   uri}
                    :on-success [::entities/apply-changes]
                    :notify     {:success :api-key-ui/update-success}}}))

(rf/reg-event-db ::set-redirect-uri-modal-data
  (fn [db [_ k v]]
    (assoc-in db [::redirect-uri-modal-data k] v)))

(rf/reg-event-db ::close-redirect-uri-modal
  (fn [db _]
    (dissoc db ::redirect-uri-modal-data)))

(rf/reg-sub ::redirect-uri-modal-data :-> ::redirect-uri-modal-data)

(defn editable-value [tr k api-key]
  (r/with-let [*editing? (r/atom false)
               *value    (r/atom (get api-key k))]
    (let [numeric?       (= k :api-key/maximum-token-lifetime)
          original-value (get api-key k)]
      [:div.flex.items-center
       (if @*editing?
         [:div.flex.flex-wrap.items-end.-mt-1.w-full
          [(if numeric?
             ui/number-input
             ui/text-input)
           (merge {:id        (str (name k) "-edit-" (:db/id api-key))
                   :value     @*value
                   :on-change #(reset! *value (futil/tval %))}
                  (when numeric?
                    {:unit-label "s"}))]
          [ui-config-pills/add-cancel-buttons
           (util/mmap {:on-add    #(do (reset! *editing? false)
                                       (>evt [::update-attribute {:db/id (:db/id api-key)
                                                                  k      (cond-> @*value
                                                                           numeric? parse-long)}]))
                       :on-cancel #(do (reset! *editing? false)
                                       (reset! *value original-value))
                       :disabled? (= original-value @*value)}
                      tr)]]
         [:<>
          [:span.mr-1 (if original-value
                        (if (= k :api-key/maximum-token-lifetime)
                          (str original-value " s")
                          original-value)
                        (tr [:common/not-set]))]
          (cc/icon-button {:render-icon      Edit24
                           :kind             :ghost
                           :size             :small
                           :icon-description (tr [:api-key-ui/edit-description])
                           :on-click         #(reset! *editing? true)})])])))

(defn redirect-uris [tr {:api-key/keys [redirect-uris client-id] :as api-key}]
  (if (seq redirect-uris)
    (for-frag [uri (sort redirect-uris)]
      [:div.flex.items-center
       [:div.break-all uri]
       [ui/modal-confirmation-button
        {:id                  (str "remove-" uri)
         :tr                  tr
         :modal-title         (tr [:admin.api-keys/remove-redirect-uri])
         :trigger-button-opts {:kind             :ghost
                               :size             :small
                               :icon-position    :right
                               :icon             i/remove
                               :icon-description (tr [:admin.api-keys/remove-redirect-uri])
                               :class-name       "only-icon-button"}
         :trigger-button-text ""
         :on-yes-click        #(>evt [::delete-redirect-uri (:db/id api-key) uri])}
        (tr [:admin.api-keys/remove-redirect-uri-modal]
            [uri client-id])]])
    [:div (tr [:common/not-set])]))

(defn add-redirect-uri-modal [tr]
  (let [{:keys [uri] :as modal-data} (<sub [::redirect-uri-modal-data])]
    [ui-modal/composed-input-modal
     {:open (seq modal-data)
      :id   "add-redirect-uri-modal"}
     (cc/modal-header
       {:button-on-click #(>evt [::close-redirect-uri-modal])}
       [:h2 (tr [:admin.api-keys/add-redirect-uri])])
     (cc/modal-body
       {}
       [ui/text-input {:id         "new-redirect-uri"
                       :value      uri
                       :label-text (tr [:admin.api-keys/redirect-uri-label])
                       :on-change  #(>evt [::set-redirect-uri-modal-data :uri (futil/tval %)])}])
     (cc/modal-footer
       {:primary-button-text     (tr [:ok])
        :secondary-button-text   (tr [:cancel])
        :primary-button-disabled (str/blank? uri)
        :on-request-submit       #(>evt [::add-redirect-uri modal-data])
        :on-request-close        #(>evt [::close-redirect-uri-modal])})]))

(defn api-key-table [{:keys [tr api-keys update-success-evt delete-success-evt]}]
  (let [secret-visible? (some :api-key/client-secret api-keys)]
    [ui-table/modern-responsive-table
     {:class        "w-full"
      :cols         [[:api-key/description
                      (fn [api-key]
                        [editable-value tr :api-key/description api-key])
                      {:flex "1 1 100px"}]
                     [:api-key/client-id
                      (fn [{:api-key/keys [client-id]}]
                        [:div.flex.items-center
                         [:span.mr-1.font-mono client-id]
                         (cc/copy-button {:on-click #(clipboard/copy-text client-id)})])
                      {:min-width "340px"}]
                     [:api-key/client-secret
                      (fn [{:api-key/keys [client-secret]}]
                        (if client-secret
                          [:div
                           [:div.flex.items-center
                            [:span.mr-1.font-mono client-secret]
                            (cc/copy-button {:on-click #(clipboard/copy-text client-secret)})]
                           [:div.text-xs.font-bold (tr [:api-key-ui/display-once-warning])]]
                          (str "<" (tr [:api-key-ui/secret-hidden]) ">")))
                      (if secret-visible?
                        {:min-width "320px"}
                        {:width "120px"})]
                     [:api-key/maximum-token-lifetime
                      (fn [api-key]
                        [editable-value tr :api-key/maximum-token-lifetime api-key])
                      {:max-width "230px"}]
                     [:api-key/use-mini-login?
                      (fn [{:db/keys [id] :api-key/keys [use-mini-login?]}]
                        (cc/checkbox {:id         (str "api-key-" id "-use-mini-login")
                                      :checked    (boolean use-mini-login?)
                                      :label-text (tr [:api-key/use-mini-login?])
                                      :hide-label true
                                      :on-change  #(>evt [::update-attribute {:db/id                   id
                                                                              :api-key/use-mini-login? %}])}))
                      {:width "120px"}]
                     [:api-key/redirect-uris
                      (fn [api-key]
                        [redirect-uris tr api-key])
                      {:flex "2 1 0%"}]]
      :make-actions (fn [{:db/keys [id]}]
                      [{:title    (tr [:admin.api-keys/add-redirect-uri])
                        :on-click #(>evt [::set-redirect-uri-modal-data :db/id id])}
                       {:title           (tr [:api-key-ui/regenerate-secret])
                        :on-click        #(>evt [::regenerate-client-secret id update-success-evt])
                        :confirm?        true
                        :confirm-content (tr [:api-key-ui/regenerate-secret-confirm])}
                       {:title           (tr [:api-key-ui/delete-key])
                        :on-click        #(>evt [::delete-api-key id delete-success-evt])
                        :confirm?        true
                        :confirm-content (tr [:api-key-ui/delete-key-confirm])}])
      :items        api-keys
      :tr           tr}]))

(defn view [_]
  (let [tr       (<sub [:tr])
        api-keys (<sub [::entities/entities {:entity/type  :entity.type/api-key
                                             :api-key/type :api-key.type/application-key}])]
    [:div.cloudpermit-page.admin-page
     [:div.bx--row.page-heading.justify-between
      [:h1 (tr [:view/admin.api-keys])]]
     [section/section
      "application-api-keys"
      {:tr             tr
       :heading-button (cc/button {:on-click #(>evt [::add-api-key])
                                   :icon     i/plus
                                   :kind     :primary
                                   :size     :small}
                                  (tr [:api-key-ui/add-new]))}
      :admin.api-keys/app-keys-title
      [:p.lg:w-2-3.xxl:w-1-2.pb-6
       (tr [:admin.api-keys/app-keys-guide])]
      (if (seq api-keys)
        [api-key-table
         (util/mmap tr api-keys)]
        [:div (tr [:group-admin.service-accounts/no-api-keys])])
      [add-redirect-uri-modal tr]]]))

(def route
  {:name        :view/admin.api-keys
   :view        view
   :controllers [{:start (fn [_]
                           (>evt [::fetch-api-keys]))}]})
